/**
 * @license
 * Copyright 2021 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import * as posedetection from "@tensorflow-models/pose-detection";
import { config } from "../vision-algo/env_config";
import CryptoJS from "crypto-js";

export const DEFAULT_LINE_WIDTH = 2;
export const DEFAULT_RADIUS = 4;

export const VIDEO_SIZE = {
  "3840 x 2160": { width: 3840, height: 2160 },
  "2160 x 3840": { width: 3840, height: 2160 },
  "2560 x 1440": { width: 2560, height: 1440 },
  "1920 x 1080": { width: 1920, height: 1080 },
  "1280 x 720": { width: 1280, height: 720 },
  "1080 x 1920": { width: 1080, height: 1920 },
  "640 X 480": { width: 640, height: 480 },
  "640 X 360": { width: 640, height: 360 },
  "360 X 270": { width: 360, height: 270 },
};
export const BLAZEPOSE_CONFIG = {
  maxPoses: 1,
  type: "full",
  scoreThreshold: 0.35,
  render3D: false,
  detectorModelUrl : "blazepose_model/detector_model/model.json",
  landmarkModelUrl : "blazepose_model/landmark_model/model.json"
};
export const POSENET_CONFIG = {
  maxPoses: 1,
  scoreThreshold: 0.5,
};
export const MOVENET_CONFIG = {
  maxPoses: 2,
  type: "multipose",
  minPoseScore: 0.2,
  multiPoseMaxDimension: 512,
  // customModel: 'lite-model_movenet_multipose_lightning_tflite_float16_1.tflite',
  customModel: "movenet_model/model.json",
  enableTracking: true,
  enableSmoothing: true,
};
export const STATE = {
  camera: {
    targetFPS: 30,
    sizeOption: config.resolution,
    facingMode: "user",
  },
  backend: "tfjs-webgl",
  flags: {
    WEBGL_VERSION: 2,
    WEBGL_CPU_FORWARD: true,
    WEBGL_PACK: true,
    WEBGL_FORCE_F16_TEXTURES: true,
  },
  modelConfig: BLAZEPOSE_CONFIG,
};
/**
 * This map descripes tunable flags and theior corresponding types.
 *
 * The flags (keys) in the map satisfy the following two conditions:
 * - Is tunable. For example, `IS_BROWSER` and `IS_CHROME` is not tunable,
 * because they are fixed when running the scripts.
 * - Does not depend on other flags when registering in `ENV.registerFlag()`.
 * This rule aims to make the list streamlined, and, since there are
 * dependencies between flags, only modifying an independent flag without
 * modifying its dependents may cause inconsistency.
 * (`WEBGL_RENDER_FLOAT32_CAPABLE` is an exception, because only exposing
 * `WEBGL_FORCE_F16_TEXTURES` may confuse users.)
 */
export const TUNABLE_FLAG_VALUE_RANGE_MAP = {
  WEBGL_VERSION: [1, 2],
  WASM_HAS_SIMD_SUPPORT: [true, false],
  WASM_HAS_MULTITHREAD_SUPPORT: [true, false],
  WEBGL_CPU_FORWARD: [true, false],
  WEBGL_PACK: [true, false],
  WEBGL_FORCE_F16_TEXTURES: [true, false],
  WEBGL_RENDER_FLOAT32_CAPABLE: [true, false],
  WEBGL_FLUSH_THRESHOLD: [-1, 0, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
  CHECK_COMPUTATION_FOR_ERRORS: [true, false],
};

export const BACKEND_FLAGS_MAP = {
  ["tfjs-wasm"]: ["WASM_HAS_SIMD_SUPPORT", "WASM_HAS_MULTITHREAD_SUPPORT"],
  ["tfjs-webgl"]: [
    "WEBGL_VERSION",
    "WEBGL_CPU_FORWARD",
    "WEBGL_PACK",
    "WEBGL_FORCE_F16_TEXTURES",
    "WEBGL_RENDER_FLOAT32_CAPABLE",
    "WEBGL_FLUSH_THRESHOLD",
  ],
  ["mediapipe-gpu"]: [],
};

export const MODEL_BACKEND_MAP = {
  [posedetection.SupportedModels.PoseNet]: ["tfjs-webgl"],
  [posedetection.SupportedModels.MoveNet]: ["tfjs-webgl", "tfjs-wasm"],
  [posedetection.SupportedModels.BlazePose]: ["mediapipe-gpu", "tfjs-webgl"],
};

export const TUNABLE_FLAG_NAME_MAP = {
  PROD: "production mode",
  WEBGL_VERSION: "webgl version",
  WASM_HAS_SIMD_SUPPORT: "wasm SIMD",
  WASM_HAS_MULTITHREAD_SUPPORT: "wasm multithread",
  WEBGL_CPU_FORWARD: "cpu forward",
  WEBGL_PACK: "webgl pack",
  WEBGL_FORCE_F16_TEXTURES: "enforce float16",
  WEBGL_RENDER_FLOAT32_CAPABLE: "enable float32",
  WEBGL_FLUSH_THRESHOLD: "GL flush wait time(ms)",
};
export const ASSESSMENT_STATUSES = {
  GestureDetectionStart: 0,
  IsCalibrated: false,
  assessment_Completed: false,
  //distance_calculated: false,
  Shoulder_time: "",
  Direction: "",
  debug_calibration: false,
  sentry_message: "",
  are_you_there: false,
  incomplete_test: false,
  calibration_error_state: false,
  assessment_successful_completion: false,
  assessment_error: false,
  exit_assessment_modal: false,
  current_angle: 0,
  save_incomplete_test: false,
  sitstand_assessment_started: false,
  start_detected: false, // Functional Reach: true once START (hand raise/voice) accepted for current phase
  in_ready_phase: false, // Functional Reach: true when in ready state, false when hand raised/assessment started
  retry: false, // TUG: true when retrying depth calibration after "Not Enough Space" error
};

export const DL_MODELS = {
  BlazePose: null,
  MoveNet: null,
  MessageForSentry: "",
};

export const AromMenuOptions = {
  actualAssessmentStartTime: 0,
  restartSubAssessment: false,
  restartPhase: false,
  skipPhase: false,
  exitAssessment: false,
  resume: false,
};

const ENCRYPTION_KEY =
  "f23be33f5268b00b2c57a4929afabc95953c6b5e729ee67f2848624082338660";
const IV = "8818e90d27ad1667a76b82d990d349e9";

function decrypt(ciphertext) {
  const decrypted = CryptoJS.AES.decrypt(
    ciphertext,
    CryptoJS.enc.Hex.parse(ENCRYPTION_KEY),
    {
      iv: CryptoJS.enc.Hex.parse(IV),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }
  );
  return decrypted.toString(CryptoJS.enc.Utf8);
}

export const AWS_CONFIG = {
  region: "us-west-2",
  credentials: {
    accessKeyId: decrypt("ET6yn1pc2KroYDOZq7sFN+fk0sxeDD2pGV8Oa0WML1s="),
    secretAccessKey: decrypt(
      "a179kUB9Zkm3K2emLenl9/Awhdn4FFuTxQvKDjh82NZXC/cGoFsH0IxqchBL7P5j"
    ),
  },
};

export const S3_BUCKET = "vst-proactivestrides123";
export const API_URL = "https://proactivestrides.vstpeoria.com/predict";

export const distace = {
  patientHeelToIpad: 0,
  patientFootIndexToIpad: 0,
};
